import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send,
  Bot,
  User,
  Square,
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Layers,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ProgressBar } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { formatMessageContent } from '@/lib/format-error';
import { useAutoScroll } from '@/lib/hooks/useAutoScroll';
import { useSidebar } from '../../../context/SidebarContext';

/** ~1 quiz per minute of lesson time, minimum 5. Matches calculateTargetQuizCount in @fias/db-types. */
function calculateTargetQuizCount(estimatedDurationMinutes?: number | null): number {
  const duration = estimatedDurationMinutes ?? 15;
  return Math.max(duration, 5);
}
import { useStreamingChat } from '../hooks/useStreamingChat';
import { parseMessageContent, type ContentSegment } from '../parseQuizBlocks';
import { QuizBlock } from '../QuizBlock';
import { getLessonIcon } from '../utils';
import { api } from '@/lib/api';
import type { Message, LessonProgressEntry } from '../types';

interface CurriculumLesson {
  lessonId: string;
  title: string;
  lessonType: string;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
}

interface CurriculumModule {
  moduleId: string;
  title: string;
  description: string | null;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
  lessons: CurriculumLesson[];
}

interface Curriculum {
  courseId: string;
  slug: string;
  title: string;
  description: string | null;
  difficultyLevel: string;
  estimatedDurationHours: number | null;
  tags: string[];
  status: string;
  modules: CurriculumModule[];
}

interface LessonViewProps {
  course: Curriculum;
  module: CurriculumModule;
  lesson: CurriculumLesson;
  onBack: () => void;
  onNextLesson: () => void;
  onPrevLesson: () => void;
  hasNext: boolean;
  hasPrev: boolean;
  initialProgress?: LessonProgressEntry;
  onEnroll: () => void;
}

export function LessonView({
  course,
  module,
  lesson,
  onBack,
  onNextLesson,
  onPrevLesson,
  hasNext,
  hasPrev,
  initialProgress,
  onEnroll,
}: LessonViewProps) {
  const { isMobile } = useSidebar();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState('');
  const { containerRef, anchorRef, scrollToBottomIfNeeded } = useAutoScroll();

  // Quiz progress state
  const targetQuizCount = calculateTargetQuizCount(lesson.estimatedDurationMinutes);
  const [correctCount, setCorrectCount] = useState(initialProgress?.correctCount ?? 0);
  const [pendingQuiz, setPendingQuiz] = useState(false);
  const progressPercent = Math.min(Math.round((correctCount / targetQuizCount) * 100), 100);

  // Track which quizzes have been answered
  const [answeredQuizzes, setAnsweredQuizzes] = useState<Set<string>>(new Set());

  // Track whether the last completed response was a deep-dive (no quiz)
  const [awaitingPostDeepDive, setAwaitingPostDeepDive] = useState(false);

  const {
    messages,
    setMessages,
    isStreaming,
    sendMessage,
    stopStreaming,
    currentAssistantMessageId,
  } = useStreamingChat({
    extraInput: {
      courseContext: {
        courseId: course.courseId,
        courseTitle: course.title,
        moduleTitle: module.title,
      },
      lessonContext: {
        lessonId: lesson.lessonId,
        lessonTitle: lesson.title,
        lessonType: lesson.lessonType,
        estimatedDurationMinutes: lesson.estimatedDurationMinutes,
      },
    },
  });

  // Enroll on mount (guarded by onEnroll callback)
  useEffect(() => {
    onEnroll();
  }, [onEnroll]);

  // Auto-start the lesson with an introduction
  useEffect(() => {
    const introMessage: Message = {
      id: `assistant-intro-${Date.now()}`,
      role: 'assistant',
      content: `Welcome to **${lesson.title}**!\n\nThis is part of the module "${module.title}" in ${course.title}.\n\nI'll be your AI tutor for this lesson. Feel free to ask questions, request examples, or ask me to explain concepts in different ways.\n\nWhen you're ready, type "start" or ask me anything about the topic!`,
      isStreaming: false,
    };
    setMessages([introMessage]);
    setCorrectCount(initialProgress?.correctCount ?? 0);
    setAnsweredQuizzes(new Set());
    setPendingQuiz(false);
    setAwaitingPostDeepDive(false);
  }, [
    lesson.lessonId,
    lesson.title,
    module.title,
    course.title,
    initialProgress?.correctCount,
    setMessages,
  ]);

  // Detect pending quizzes in the latest assistant message
  useEffect(() => {
    const lastAssistant = [...messages]
      .reverse()
      .find((m) => m.role === 'assistant' && !m.isStreaming && m.content);
    if (!lastAssistant) return;

    const segments = parseMessageContent(formatMessageContent(lastAssistant.content).content);
    const hasUnansweredQuiz = segments.some((seg, idx) => {
      if (seg.type !== 'quiz') return false;
      const quizKey = `${lastAssistant.id}:${idx}`;
      return !answeredQuizzes.has(quizKey);
    });
    setPendingQuiz(hasUnansweredQuiz);
  }, [messages, answeredQuizzes]);

  // Detect deep-dive completions
  useEffect(() => {
    if (currentAssistantMessageId) return; // Still streaming
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant' || lastMsg.isStreaming) return;
    const idx = messages.length - 1;
    if (idx > 0) {
      const preceding = messages[idx - 1];
      if (preceding.role === 'user' && preceding.content.startsWith('[GO_DEEPER]')) {
        setAwaitingPostDeepDive(true);
      }
    }
  }, [messages, currentAssistantMessageId]);

  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, scrollToBottomIfNeeded]);

  useEffect(() => {
    if (!isStreaming && !pendingQuiz && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isStreaming, pendingQuiz, isMobile]);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // Persist progress to backend
  const persistProgress = useCallback(
    (newCorrectCount: number) => {
      const newPercent = Math.min(Math.round((newCorrectCount / targetQuizCount) * 100), 100);
      const status = newPercent >= 100 ? 'completed' : 'in_progress';
      api
        .updateLessonProgress(lesson.lessonId, {
          correctCount: newCorrectCount,
          targetQuizCount,
          status,
        })
        .catch(() => {
          // Best-effort persistence
        });
    },
    [lesson.lessonId, targetQuizCount],
  );

  const handleQuizCorrect = useCallback(
    (quizKey: string) => {
      if (answeredQuizzes.has(quizKey)) return;
      setAnsweredQuizzes((prev) => new Set(prev).add(quizKey));
      setCorrectCount((prev) => {
        const next = prev + 1;
        persistProgress(next);
        return next;
      });
    },
    [answeredQuizzes, persistProgress],
  );

  const handleContinue = useCallback(() => {
    if (isStreaming) return;
    setAwaitingPostDeepDive(false);
    sendMessage('Continue');
  }, [isStreaming, sendMessage]);

  const handleGoDeeper = useCallback(() => {
    if (isStreaming) return;
    setAwaitingPostDeepDive(false);
    sendMessage(
      '[GO_DEEPER] Please elaborate more on the topic you just taught. Provide additional detail, examples, or nuance.',
    );
  }, [isStreaming, sendMessage]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isStreaming || pendingQuiz) return;
    setInputValue('');
    await sendMessage(trimmedInput);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Render assistant message content with inline quizzes
  const renderAssistantContent = useCallback(
    (message: Message, isLastMessage: boolean) => {
      const formatted = formatMessageContent(message.content).content;
      const segments: ContentSegment[] = parseMessageContent(formatted);
      const hasQuiz = segments.some((s) => s.type === 'quiz');

      return (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {segments.map((segment, idx) => {
            if (segment.type === 'text') {
              return <ReactMarkdown key={idx}>{segment.content}</ReactMarkdown>;
            }
            if (segment.type === 'loading') {
              return (
                <div
                  key={idx}
                  className="my-4 flex items-center gap-2 text-muted-foreground text-sm"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading quiz...
                </div>
              );
            }
            const quizKey = `${message.id}:${idx}`;
            return (
              <QuizBlock
                key={quizKey}
                data={segment.data}
                onCorrect={() => handleQuizCorrect(quizKey)}
                onContinue={handleContinue}
                onGoDeeper={handleGoDeeper}
              />
            );
          })}
          {message.isStreaming && message.content && (
            <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
          )}
          {/* Standalone Continue + Go Deeper buttons after a deep-dive response */}
          {isLastMessage && !message.isStreaming && awaitingPostDeepDive && !hasQuiz && (
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={handleGoDeeper} className="gap-1.5">
                <Layers className="h-3.5 w-3.5" />
                Go Deeper
              </Button>
              <Button size="sm" onClick={handleContinue} className="gap-1.5">
                Continue
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      );
    },
    [handleQuizCorrect, handleContinue, handleGoDeeper, awaitingPostDeepDive],
  );

  const LessonIcon = getLessonIcon(lesson.lessonType);
  const inputDisabled = isStreaming || pendingQuiz;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={onBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <LessonIcon className="h-4 w-4 text-primary" />
                  <h1 className="font-semibold">{lesson.title}</h1>
                </div>
                <p className="text-xs text-muted-foreground">
                  {module.title} - {course.title}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onPrevLesson} disabled={!hasPrev}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={onNextLesson} disabled={!hasNext}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="mt-2">
            <ProgressBar value={progressPercent} />
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div ref={containerRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="space-y-6">
            {messages.map((message, msgIdx) => {
              const isLastMessage = msgIdx === messages.length - 1;
              const displayContent =
                message.role === 'user' && message.content.startsWith('[GO_DEEPER]')
                  ? 'Go Deeper'
                  : message.content;

              return (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-4',
                    message.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[80%] rounded-2xl px-4 py-3',
                      message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted',
                    )}
                  >
                    {message.role === 'assistant' && (
                      <div className="text-xs text-muted-foreground mb-1">AI Tutor</div>
                    )}
                    <div className="break-words">
                      {message.isStreaming && !message.content ? (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <div className="flex gap-0.5">
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: '0ms' }}
                            />
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: '150ms' }}
                            />
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: '300ms' }}
                            />
                          </div>
                          <span className="text-sm">Preparing your lesson...</span>
                        </div>
                      ) : message.role === 'assistant' ? (
                        renderAssistantContent(message, isLastMessage)
                      ) : (
                        <>
                          {displayContent}
                          {message.isStreaming && message.content && (
                            <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={anchorRef} />
          </div>
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border bg-background">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <form onSubmit={handleSubmit} className="relative">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                pendingQuiz
                  ? 'Answer the question above to continue'
                  : 'Ask a question about this lesson...'
              }
              disabled={inputDisabled}
              className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
              rows={1}
              style={{ height: 'auto' }}
            />
            {isStreaming ? (
              <Button
                type="button"
                size="icon"
                variant="destructive"
                onClick={stopStreaming}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
              >
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                disabled={!inputValue.trim() || pendingQuiz}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </form>
          <p className="text-xs text-muted-foreground text-center mt-2">
            {pendingQuiz
              ? 'Complete the quiz to continue the lesson'
              : 'Press Enter to send, Shift+Enter for new line'}
          </p>
        </div>
      </div>
    </div>
  );
}
